// src/services/event-service.js
// Client-side service for events CRUD operations.
// Events live at practitioners/{uid}/events/{eventId}
// Registrations at practitioners/{uid}/events/{eventId}/registrations/{regId}
// Event slugs at eventSlugs/{practitionerSlug}_{eventSlug}

import { db } from '../config/firebase-config';
import {
  collection, doc, getDocs, getDoc, deleteDoc,
  query, where, orderBy, onSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions(undefined, 'us-west1');

// ─── Slug Utility ───────────────────────────────────────────────────────────

/**
 * Generate a URL-friendly slug from an event title.
 * Handles special characters, spaces, and ensures clean output.
 */
export function generateEventSlug(title) {
  if (!title) return '';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')         // remove special characters
    .replace(/[\s_]+/g, '-')          // spaces/underscores to hyphens
    .replace(/-+/g, '-')              // collapse multiple hyphens
    .replace(/^-|-$/g, '');           // trim leading/trailing hyphens
}

// ─── Default Event Data ─────────────────────────────────────────────────────

/**
 * Returns a blank event document shape with defaults.
 * Used by EventCreationForm for initial state.
 */
export function getDefaultEventData() {
  return {
    // Core details
    title: '',
    description: '',
    eventType: 'single',       // 'single' | 'series' | 'multi_day'
    coverImage: null,
    slug: '',
    status: 'draft',           // 'draft' | 'published' | 'cancelled' | 'completed'
    modalities: [],

    // Scheduling
    schedule: {
      date: null,
      startTime: null,
      endTime: null,
      recurringDay: null,
      recurringTime: null,
      sessionCount: null,
      sessionDuration: null,
      startDate: null,
      excludedDates: [],
      sessionDates: [],
      endDate: null,
      dailySchedule: null,
    },
    timezone: 'America/Los_Angeles',

    // Format & Location
    format: 'virtual',         // 'virtual' | 'in_person' | 'hybrid'
    venue: null,               // { name, address, directions }
    zoomMeetingId: null,
    zoomJoinUrl: null,
    manualMeetingLink: null,

    // Capacity
    maxAttendees: 20,
    currentAttendees: 0,

    // Pricing
    pricingModel: 'flat',      // 'flat' | 'early_bird' | 'deposit' | 'free'
    pricing: {
      price: null,
      earlyBirdPrice: null,
      earlyBirdDeadline: null,
      depositAmount: null,
      totalPrice: null,
      remainderDueDays: null,
    },

    // Registration
    registrationDeadline: null,
    intakeFormId: null,

    // Reminders
    reminders: {
      oneWeek: true,
      threeDays: false,
      twentyFourHours: true,
      twoHours: false,
    },

    // Post-event
    thankYouMessage: null,
    recordingUrl: null,
    recordingExpiry: null,
    recordingShared: false,

    // Google Calendar
    googleCalendarEventIds: [],

    // Metadata (set by Cloud Function)
    createdAt: '',
    updatedAt: '',
    publishedAt: null,
    completedAt: null,
    cancelledAt: null,
  };
}

// ─── Cloud Function Callers ─────────────────────────────────────────────────

/**
 * Create a new event via Cloud Function.
 * Returns { success, eventId, slug }.
 */
export async function createEvent(eventData) {
  const fn = httpsCallable(functions, 'createEvent');
  const result = await fn(eventData);
  return result.data;
}

/**
 * Update an existing event via Cloud Function.
 * Returns { success, eventId }.
 */
export async function updateEvent(eventId, eventData) {
  const fn = httpsCallable(functions, 'updateEvent');
  const result = await fn({ eventId, ...eventData });
  return result.data;
}

/**
 * Publish an event (change status from draft to published).
 * Returns { success, eventId }.
 */
export async function publishEvent(eventId) {
  const fn = httpsCallable(functions, 'publishEvent');
  const result = await fn({ eventId });
  return result.data;
}

// ─── Registration ───────────────────────────────────────────────────────────

/**
 * Register for an event. Free events register immediately.
 * Paid events return { checkoutUrl } for Stripe redirect.
 */
export async function registerForEvent(practitionerId, eventId) {
  const fn = httpsCallable(functions, 'registerForEvent');
  const result = await fn({ practitionerId, eventId });
  return result.data;
}

/**
 * Confirm event registration after successful Stripe payment.
 */
export async function confirmEventRegistration(practitionerId, eventId, registrationId) {
  const fn = httpsCallable(functions, 'confirmEventRegistration');
  const result = await fn({ practitionerId, eventId, registrationId });
  return result.data;
}

/**
 * Cancel own event registration (attendee).
 */
export async function cancelEventRegistration(practitionerId, eventId, registrationId) {
  const fn = httpsCallable(functions, 'cancelEventRegistration');
  const result = await fn({ practitionerId, eventId, registrationId });
  return result.data;
}

/**
 * Remove an attendee from event (practitioner).
 */
export async function removeEventAttendee(eventId, registrationId) {
  const fn = httpsCallable(functions, 'removeEventAttendee');
  const result = await fn({ eventId, registrationId });
  return result.data;
}

/**
 * Export attendees as CSV (practitioner).
 */
export async function exportEventAttendees(eventId) {
  const fn = httpsCallable(functions, 'exportEventAttendees');
  const result = await fn({ eventId });
  return result.data;
}

/**
 * Cancel an entire event (practitioner). Refunds all attendees.
 */
export async function cancelEvent(eventId, reason) {
  const fn = httpsCallable(functions, 'cancelEvent');
  const result = await fn({ eventId, reason });
  return result.data;
}

/**
 * Send group message to all event attendees (practitioner).
 */
export async function sendEventGroupMessage(eventId, subject, message) {
  const fn = httpsCallable(functions, 'sendEventGroupMessage');
  const result = await fn({ eventId, subject, message });
  return result.data;
}

/**
 * Share recording with all event attendees (practitioner).
 */
export async function shareEventRecording(eventId, recordingUrl, expiry) {
  const fn = httpsCallable(functions, 'shareEventRecording');
  const result = await fn({ eventId, recordingUrl, expiry });
  return result.data;
}

/**
 * Check if current user is registered for an event.
 */
export async function checkRegistrationStatus(practitionerId, eventId, userId) {
  if (!db || !practitionerId || !eventId || !userId) return null;
  try {
    const q = query(
      collection(db, 'practitioners', practitionerId, 'events', eventId, 'registrations'),
      where('attendeeId', '==', userId),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    // Return the most relevant registration (registered > pending_payment > cancelled)
    const regs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return regs.find(r => r.status === 'registered') || regs.find(r => r.status === 'pending_payment') || regs[0];
  } catch (err) {
    console.warn('checkRegistrationStatus error:', err);
    return null;
  }
}

// ─── Firestore Reads ────────────────────────────────────────────────────────

/**
 * Subscribe to all events for a practitioner (realtime).
 * Returns an unsubscribe function.
 */
export function subscribeToEvents(practitionerId, callback) {
  if (!db || !practitionerId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, 'practitioners', practitionerId, 'events'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(events);
  }, (err) => {
    console.error('subscribeToEvents error:', err);
    callback([]);
  });
}

/**
 * Get a single event by ID.
 */
export async function getEventById(practitionerId, eventId) {
  if (!db || !practitionerId || !eventId) return null;
  try {
    const snap = await getDoc(doc(db, 'practitioners', practitionerId, 'events', eventId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('getEventById error:', err);
    return null;
  }
}

/**
 * Subscribe to registrations for an event (realtime).
 * Returns an unsubscribe function.
 */
export function subscribeToRegistrations(practitionerId, eventId, callback) {
  if (!db || !practitionerId || !eventId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(db, 'practitioners', practitionerId, 'events', eventId, 'registrations'),
    orderBy('registeredAt', 'desc'),
  );
  return onSnapshot(q, (snap) => {
    const regs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(regs);
  }, (err) => {
    console.error('subscribeToRegistrations error:', err);
    callback([]);
  });
}

/**
 * Delete a draft event (only drafts can be deleted).
 */
export async function deleteDraftEvent(practitionerId, eventId) {
  if (!db || !practitionerId || !eventId) throw new Error('Missing parameters');
  const eventDoc = await getDoc(doc(db, 'practitioners', practitionerId, 'events', eventId));
  if (!eventDoc.exists()) throw new Error('Event not found');
  if (eventDoc.data().status !== 'draft') throw new Error('Only draft events can be deleted');
  await deleteDoc(doc(db, 'practitioners', practitionerId, 'events', eventId));
}
